import { type TFunction } from 'i18next';
import { type CountryCodes, type CurrencyCodes, formatCurrency } from 'src/shared/utils/currency';
import { type FlexflowTransaction, TRANSACTION_ORIGIN, TRANSACTION_TYPE, WALLET_TRANSACTION_TYPE } from '../types';
import { mapApiEnumValueToTranslationKey } from './translationKeys';

const isAccumulatedOrigin = (origin?: TRANSACTION_ORIGIN): boolean =>
  origin === TRANSACTION_ORIGIN.FR_ACCUMULATED || origin === TRANSACTION_ORIGIN.SB_ACCUMULATED;

const formatAbsoluteAmount = (locale: CountryCodes, currency: CurrencyCodes, value: number): string =>
  formatCurrency(locale, currency, 2)(Math.abs(Number(value)));

const MIXED_FUNDING_SOURCES: Array<{
  origin: TRANSACTION_ORIGIN;
  getAmount: (row: FlexflowTransaction) => number | undefined;
}> = [
  {
    origin: TRANSACTION_ORIGIN.SB_ACCUMULATED,
    getAmount: row => row.amountFromSbAccumulated
  },
  {
    origin: TRANSACTION_ORIGIN.FR_ACCUMULATED,
    getAmount: row => row.amountFromFrAccumulated
  },
  {
    origin: TRANSACTION_ORIGIN.FR_CYCLE,
    getAmount: row => row.amountFromFrCycle
  }
];

const isAmountPresent = (value: number | undefined): value is number => value !== undefined && value !== null;

const buildSplitTooltipFallback = (
  row: FlexflowTransaction,
  t: TFunction,
  locale: CountryCodes,
  currency: CurrencyCodes
): string =>
  t('amount.tooltip.split', {
    sbAccumulatedAmount: formatAbsoluteAmount(locale, currency, Number(row.amountFromSbAccumulated ?? 0)),
    frAccumulatedAmount: formatAbsoluteAmount(locale, currency, Number(row.amountFromFrAccumulated ?? 0)),
    frCycleAmount: formatAbsoluteAmount(locale, currency, Number(row.amountFromFrCycle ?? 0))
  });

export const getAmountTooltipText = (
  row: FlexflowTransaction,
  t: TFunction,
  locale: CountryCodes,
  currency: CurrencyCodes
): string | null => {
  if (row.transactionType === TRANSACTION_TYPE.CREDIT) {
    return null;
  }

  if (isAccumulatedOrigin(row.transactionOrigin)) {
    return t('amount.tooltip.full wallet withdraw');
  }

  if (row.transactionOrigin !== TRANSACTION_ORIGIN.MIXED) {
    return null;
  }

  if (row.walletTransactionType !== WALLET_TRANSACTION_TYPE.WITHDRAW) {
    return null;
  }

  const usedSources = MIXED_FUNDING_SOURCES.filter(source => {
    const amount = source.getAmount(row);
    return isAmountPresent(amount) && Number(amount) !== 0;
  });

  if (usedSources.length > 0) {
    return usedSources
      .map(source => {
        const label = t(`origin.values.${mapApiEnumValueToTranslationKey(source.origin)}`);
        const amount = formatAbsoluteAmount(locale, currency, Number(source.getAmount(row)));
        return `${label}: ${amount}`;
      })
      .join(', ');
  }

  // Breakdown fields omitted by the API — keep a tooltip rather than hiding it.
  const allBreakdownFieldsMissing = MIXED_FUNDING_SOURCES.every(source => !isAmountPresent(source.getAmount(row)));
  if (allBreakdownFieldsMissing && Number(row.amount) !== 0) {
    return buildSplitTooltipFallback(row, t, locale, currency);
  }

  return null;
};
